import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Checkbox,
  Button,
  CircularProgress,
  Tooltip,
  InputAdornment,
  OutlinedInput,
  LinearProgress,
  Skeleton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LockIcon from "@mui/icons-material/LockOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpenOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import Sparkline from "./Sparkline";
import InlineMonthly from "./InlineMonthly";
import { fmtK, fmtPct, fmtDelta, paceColor, paceBg } from "../utils";
import { fmtDuration } from "./RunForecastDialog";

const STALE_TEXT = {
  new_actuals: "a month has closed since (new actuals)",
  inventory_changed: "stock on hand has changed",
  promo_changed: "promo days or forecast settings have changed",
};

const isFinished = (r) => r && (r.status === "done" || r.status === "partial");

/** "120/400 SKUs · about 3 min left" for a running run. */
const runProgress = (run) => {
  const done = run.done_count || 0;
  const left = Math.max(0, (run.est_seconds || 0) - (Date.now() - new Date(run.started_at).getTime()) / 1000);
  return `${done}/${run.sku_count} SKUs · ${left > 0 ? fmtDuration(left) + " left" : "finishing…"}`;
};

/** Shown next to the category name while its forecast runs. */
function RunningChip({ run }) {
  return (
    <Tooltip title={`Forecasting ${runProgress(run)}. Cancel from the ⋮ menu.`}>
      <Chip
        size="small"
        variant="outlined"
        icon={<CircularProgress size={10} sx={{ ml: "6px !important" }} />}
        label={`Forecasting ${run.done_count || 0}/${run.sku_count}`}
        sx={{ fontSize: 10, height: 20, borderColor: "#1976d2", color: "#1976d2" }}
      />
    </Tooltip>
  );
}

/**
 * Row actions for a category, behind a ⋮ button: Run Forecast (Cancel while a
 * run is in progress), View Forecast and Email Excel (only when a finished run
 * exists — a cancelled or failed run leaves the previous one in place) and the
 * category's monthly view. Under Run / Cancel: progress while running, else
 * the date of the forecast shown and a stale flag when it no longer matches
 * the data (re-running stays the planner's choice).
 */
function RowActionsMenu({
  run,
  onRun,
  onView,
  onCancel,
  onEmail,
  hideForecast = false, // channel view: only Monthly view
  monthlyOpen,
  onToggleMonthly,
  textSecondary,
}) {
  const [anchor, setAnchor] = useState(null);
  const running = run?.status === "running";
  const shown = isFinished(run) ? run : run?.last_finished || null;
  const viewable = !running && !!shown;
  const close = () => setAnchor(null);
  const pick = (fn) => () => {
    close();
    fn();
  };

  let note = null;
  if (running) {
    note = runProgress(run);
  } else if (run?.status === "error") {
    note = "Last run failed";
  } else if (run?.status === "cancelled") {
    note = "Last run cancelled";
  } else if (viewable) {
    note = `Last run ${new Date(shown.finished_at || shown.started_at).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    })}`;
  }
  const staleTitle =
    run?.status === "error"
      ? run.error || ""
      : shown?.stale
        ? `Stale: ${(shown.stale_reasons || []).map((r) => STALE_TEXT[r] || r).join("; ")}. Run again to refresh.`
        : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: textSecondary }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!hideForecast && (running ? (
          <MenuItem onClick={pick(() => onCancel(run))} sx={{ fontSize: 13, color: "#c62828" }}>
            <ListItemIcon>
              <StopCircleOutlinedIcon fontSize="small" sx={{ color: "#c62828" }} />
            </ListItemIcon>
            <ListItemText
              primary="Cancel forecast"
              secondary={note}
              primaryTypographyProps={{ fontSize: 13 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        ) : (
          <MenuItem onClick={pick(onRun)} sx={{ fontSize: 13 }}>
            <ListItemIcon>
              <PlayArrowOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Run Forecast"
              secondary={
                note && (
                  <span title={staleTitle}>
                    {note}
                    {shown?.stale && (
                      <span style={{ marginLeft: 4, color: "#ed6c02", fontWeight: 700 }}>· stale</span>
                    )}
                  </span>
                )
              }
              primaryTypographyProps={{ fontSize: 13 }}
              secondaryTypographyProps={{
                fontSize: 11,
                color: run?.status === "error" ? "#c62828" : undefined,
              }}
            />
          </MenuItem>
        ))}
        {!hideForecast && (
          <MenuItem disabled={!viewable} onClick={pick(() => onView(shown))} sx={{ fontSize: 13 }}>
            <ListItemIcon>
              <InsightsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            View Forecast
          </MenuItem>
        )}
        {!hideForecast && (
          <MenuItem disabled={!viewable} onClick={pick(() => onEmail(shown))} sx={{ fontSize: 13 }}>
            <ListItemIcon>
              <ForwardToInboxOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Email Excel
          </MenuItem>
        )}
        <MenuItem onClick={pick(onToggleMonthly)} sx={{ fontSize: 13 }}>
          <ListItemIcon>
            <CalendarViewMonthOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {monthlyOpen ? "Hide monthly view" : "Monthly view"}
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default function PlannerTable({
  categories,
  filteredCategories,
  filterTabs,
  activeFilter,
  setActiveFilter,
  planValues,
  locked,
  expanded,
  scenario,
  setScenario,
  toggleExpand,
  expandAll,
  collapseAll,
  handlePlanChange,
  toggleLock,
  currentYearData,
  currentMonth,
  totalLY,
  allocated,
  totalYearProj,
  yearProjVsPlanPct,
  darkMode,
  loading,
  categoryStatuses,
  onCategoryStatusToggle,
  planId,
  clientId,
  forecastErrors = {},
  forecastPending = false,
  planMonthlyById = {},
  closedMonth = 0,
  savedRowMeta = {},
  onPlanMonthlyChange,
  forecastRuns = {},
  runForecastByLeaf = {},
  onRunForecast,
  onViewForecast,
  onCancelForecast,
  onEmailForecast,
  // A channel's view of the plan: plans, locks and forecasts can't be changed here.
  readOnly = false,
  // Channel filter (beside Monthly View): the plan per channel from the plan's GET.
  channelView = [],
  channelStatus = "ready",
  activeChannel = "ALL",
  onChannelSelect,
}) {
  // Status column is hidden for now.
  const SHOW_STATUS = false;
  const spanAll = SHOW_STATUS ? 9 : 8;
  const spanTail = SHOW_STATUS ? 3 : 2;

  // ── Local monthly-expansion state ────────────────────────────────────────
  const [monthlyExpanded, setMonthlyExpanded] = useState({});
  // Actual + Projection rows in the monthly breakdown are shown by default;
  // the "Show" checkboxes let you hide them.
  const [showActual, setShowActual] = useState(true);
  const [showForecast, setShowForecast] = useState(true);

  const toggleMonthly = (id) =>
    setMonthlyExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // A category's monthly view: its own grid for a leaf row, else every
  // sub-category's grid (opening the category so they're visible).
  const isMonthlyOpen = (parent) => {
    const leaves = parent.children?.length ? parent.children : [parent];
    return leaves.every((c) => monthlyExpanded[c._id.toString()]);
  };
  const toggleAllMonthly = (parent) => {
    const parentId = parent._id.toString();
    const children = parent.children || [];
    if (!children.length) {
      toggleMonthly(parentId);
      return;
    }
    const open = !isMonthlyOpen(parent);
    if (open && !expanded[parentId]) toggleExpand(parentId);
    setMonthlyExpanded((prev) => ({
      ...prev,
      ...Object.fromEntries(children.map((c) => [c._id.toString(), open])),
    }));
  };

  const allMonthlyExpanded = categories.every((parent) => {
    const leaves = parent.children?.length
      ? parent.children
      : [parent];
    return leaves.every((c) => monthlyExpanded[c._id.toString()]);
  });

  const toggleAllMonthlyGlobal = () => {
    if (allMonthlyExpanded) {
      collapseAll();
      setMonthlyExpanded({});
    } else {
      expandAll();
      const next = {};
      categories.forEach((parent) => {
        (parent.children || []).forEach((child) => {
          next[child._id.toString()] = true;
        });
        if (!parent.children?.length) {
          next[parent._id.toString()] = true;
        }
      });
      setMonthlyExpanded(next);
    }
  };
  // ── Style helpers ────────────────────────────────────────────────────────
  const parentRowBg = darkMode ? "#262626" : "#f9f9f9";
  const childRowBg = darkMode ? "#1a1a1a" : "#fff";
  const totalRowBg = darkMode ? "#1e1e1e" : "#f0f0f0";
  const headerBg = darkMode ? "#1e1e1e" : "#fafafa";
  const borderColor = darkMode ? "#333" : "#e8e8e8";
  const textPrimary = darkMode ? "#fff" : "#111";
  const textSecondary = darkMode ? "#aaa" : "#666";

  const cellSx = {
    borderBottom: `1px solid ${borderColor}`,
    py: 1,
    px: 1.5,
    fontSize: 13,
  };

  const headerCellSx = {
    ...cellSx,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: textSecondary,
    textTransform: "uppercase",
    bgcolor: headerBg,
    borderBottom: `2px solid ${borderColor}`,
    verticalAlign: "middle",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  };

  const allExpanded = categories.every((p) => expanded[p._id.toString()]);
  // Flat plan (e.g. Product Type dimension) — no row has children.
  const isFlat =
    categories.length > 0 && categories.every((p) => !p.children?.length);

  const getParentPlanTotal = (parent) => {
    if (parent.children?.length) {
      return (parent.children || []).reduce(
        (s, c) => s + (planValues[c._id.toString()] || 0),
        0,
      );
    }
    return planValues[parent._id.toString()] || 0;
  };

  // Next-year plans: LY is the current year in full — actual months plus a
  // rest-of-year forecast from this month on (set by the backend).
  const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const lyForecastFrom = categories.find((c) => c.ly_forecast_from_month)?.ly_forecast_from_month || null;
  const lyForecastRange = lyForecastFrom
    ? lyForecastFrom === 12
      ? "Dec"
      : `${MONTH_ABBR[lyForecastFrom - 1]}–Dec`
    : "";
  const lyActualRange =
    lyForecastFrom && lyForecastFrom > 1
      ? lyForecastFrom === 2
        ? "Jan"
        : `Jan–${MONTH_ABBR[lyForecastFrom - 2]}`
      : "";

  // Builds a 12-element trend array:
  //   past months  (i+1 <= currentMonth) → ytd_monthly actuals (always available)
  //   future months (i+1  > currentMonth) → forecast_monthly only after forecast completes
  const buildTrendData = (category) => {
    const actual = category.ytd_monthly || [];
    const forecast = forecastPending ? null : category.forecast_monthly;
    return Array.from({ length: 12 }, (_, i) => {
      if (i + 1 <= currentMonth) return actual[i] ?? null;
      return forecast?.[i] ?? null;
    });
  };

  // Trend cell: a loader while the background forecast runs (any month still
  // to forecast), then the sparkline — never a half-drawn or empty line.
  const renderTrend = (category) => {
    if (forecastPending && currentMonth < 12) {
      return (
        <Box sx={{ width: 80, height: 28, display: "flex", alignItems: "center", gap: 0.75 }}>
          <CircularProgress size={14} thickness={5} />
          <Typography sx={{ fontSize: 10, color: darkMode ? "#888" : "#999", letterSpacing: 0.3 }}>
            forecasting…
          </Typography>
        </Box>
      );
    }
    const color =
      category.pace_status === "AHEAD"
        ? "#4caf50"
        : category.pace_status === "BEHIND"
          ? "#f44336"
          : "#2196f3";
    return (
      <Sparkline
        data={buildTrendData(category)}
        splitAt={currentMonth}
        color={color}
        forecastColor={color}
      />
    );
  };

  // ── Monthly click handlers ─ removed; monthly state is local ────────────

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor,
        borderRadius: 2,
        bgcolor: "var(--bg-paper)",
        overflow: "hidden",
      }}
    >
      {/* ── Table header toolbar ── */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${borderColor}` }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: textPrimary,
                textAlign: "left",
              }}
            >
              {`Allocation plan · by department & sub-category`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: textSecondary, mt: 0.25 }}>
              Click dept row to expand · plan values in $K · all values shown in
              thousands
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {yearProjVsPlanPct != null && (
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 11, color: textSecondary }}>
                  Year projection
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 400, color: textPrimary }}
                >
                  {fmtK(totalYearProj)} · {yearProjVsPlanPct}% of plan
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Scenarios + Filters row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1.5,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* Scenario toggles */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant={scenario === "start-of-year" ? "contained" : "outlined"}
              onClick={() => setScenario("start-of-year")}
              sx={{
                fontSize: 11,
                textTransform: "none",
                borderRadius: 5,
                px: 2,
              }}
            >
              Start-of-year plan
            </Button>
            <Button
              size="small"
              variant={scenario === "match-ly" ? "contained" : "outlined"}
              onClick={() => setScenario("match-ly")}
              sx={{
                fontSize: 11,
                textTransform: "none",
                borderRadius: 5,
                px: 2,
              }}
            >
              Match LY actual
            </Button>
            <Button
              size="small"
              variant={allMonthlyExpanded ? "contained" : "outlined"}
              onClick={toggleAllMonthlyGlobal}
              sx={{
                fontSize: 11,
                textTransform: "none",
                borderRadius: 5,
                px: 2,
                whiteSpace: "nowrap",
              }}
            >
              Monthly View
            </Button>
            {/* Channel filter: the plan by a channel's sales ratio (read-only).
                Hidden on a new plan until it's saved; then disabled until the
                channel allocation is worked out, with the reason on hover. */}
            {channelStatus !== "unsaved" && (
              <Tooltip
                title={
                  channelStatus === "ready"
                    ? ""
                    : channelStatus === "none"
                      ? "Channel view isn't available: no channel sales found for this plan's categories in the last 12 months."
                      : "Channel view will be available once the channel allocation is done."
                }
              >
                <span style={{ display: "inline-flex" }}>
              <Select
                size="small"
                disabled={channelStatus !== "ready"}
                value={activeChannel}
                onChange={(e) => onChannelSelect?.(e.target.value)}
                inputProps={{ "aria-label": "Channel" }}
                renderValue={(v) => (v === "ALL" ? "All channels" : v)}
                sx={{
                  fontSize: 11,
                  height: 30.75,
                  borderRadius: 5,
                  minWidth: 140,
                  color: activeChannel === "ALL" ? undefined : "#1976d2",
                  "& .MuiSelect-select": { py: 0.5, pl: 2 },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: activeChannel === "ALL" ? undefined : "#1976d2",
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ fontSize: 13 }}>
                  All channels
                </MenuItem>
                {(channelView || []).map((c) => (
                  <MenuItem key={c.channel} value={c.channel} sx={{ fontSize: 13, gap: 1.5, justifyContent: "space-between" }}>
                    <span>{c.channel}</span>
                    <span style={{ color: textSecondary, fontSize: 12 }}>
                      {Math.round((c.share || 0) * 1000) / 10}% · {fmtK(c.plan)}
                    </span>
                  </MenuItem>
                ))}
              </Select>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Expand All + Category filter tabs */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {!isFlat && (
              <Button
                size="small"
                startIcon={
                  allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />
                }
                onClick={allExpanded ? collapseAll : expandAll}
                sx={{ fontSize: 11, textTransform: "none" }}
                variant="outlined"
              >
                {allExpanded ? "Collapse All" : "Expand All"}
              </Button>
            )}
            {/* Actual / Projection row toggles */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                border: `1px solid ${borderColor}`,
                borderRadius: 1,
                px: 1,
                py: 0.25,
              }}
            >
              <Typography sx={{ fontSize: 11, color: textSecondary, mr: 0.5 }}>
                Show:
              </Typography>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={showActual}
                  onChange={(e) => setShowActual(e.target.checked)}
                  sx={{ p: 0.25 }}
                />
                <Typography sx={{ fontSize: 11, color: textSecondary }}>
                  Actual
                </Typography>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={showForecast}
                  onChange={(e) => setShowForecast(e.target.checked)}
                  sx={{ p: 0.25 }}
                />
                <Typography sx={{ fontSize: 11, color: textSecondary }}>
                  Projection
                </Typography>
              </label>
            </Box>
            {filterTabs.map((tab) => (
              <Button
                key={tab}
                size="small"
                variant={activeFilter === tab ? "contained" : "text"}
                onClick={() => setActiveFilter(tab)}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  borderRadius: 5,
                  minWidth: 48,
                  px: 1.5,
                  color:
                    activeFilter === tab ? "#fff" : darkMode ? "#ccc" : "#444",
                  bgcolor: activeFilter === tab ? "#111" : "transparent",
                  "&:hover": {
                    bgcolor:
                      activeFilter === tab
                        ? "#222"
                        : darkMode
                          ? "#333"
                          : "#f0f0f0",
                  },
                }}
              >
                {tab}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Loading (the page shows PlannerSkeleton instead of this table) */}
      {loading && (
        <Box sx={{ py: 2 }}>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.75 }} />
          ))}
        </Box>
      )}

      {/* ── Table ── */}
      {!loading && (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...headerCellSx, width: SHOW_STATUS ? "23%" : "25%" }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, width: SHOW_STATUS ? "10%" : "12%" }}
                >
                  Trend
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "8%" : "9%",
                    textAlign: "right",
                  }}
                >
                  {lyForecastFrom ? (
                    <Tooltip
                      arrow
                      title={`Last year in full: ${lyActualRange ? `${lyActualRange} actual sales + ` : ""}${lyForecastRange} forecast, because ${currentYearData - 1} isn't over yet.`}
                    >
                      <Box component="span" sx={{ cursor: "help" }}>
                        LY
                        <Box
                          component="span"
                          sx={{ display: "block", fontSize: 9.5, fontWeight: 500, textTransform: "none", letterSpacing: 0, color: textSecondary }}
                        >
                          incl. forecast {lyForecastRange}
                        </Box>
                      </Box>
                    </Tooltip>
                  ) : (
                    "LY"
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "11%" : "13%",
                    textAlign: "right",
                  }}
                >
                  Plan CY{currentYearData}
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "13%" : "14%",
                    textAlign: "right",
                  }}
                >
                  YTD Actual
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "11%" : "12%",
                    textAlign: "right",
                  }}
                >
                  Year Proj
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "9%" : "8%",
                    textAlign: "center",
                  }}
                >
                  Pace
                </TableCell>
                {SHOW_STATUS && (
                  <TableCell
                    sx={{ ...headerCellSx, width: "9%", textAlign: "center" }}
                  >
                    Status
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    ...headerCellSx,
                    width: SHOW_STATUS ? "8%" : "7%",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredCategories.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={spanAll}
                    sx={{ textAlign: "center", py: 6, color: textSecondary }}
                  >
                    No data available
                  </TableCell>
                </TableRow>
              )}

              {filteredCategories.map((parent) => {
                const parentId = parent._id.toString();
                const isExpanded = !!expanded[parentId];
                const parentPlan = getParentPlanTotal(parent);
                const parentYTDPct =
                  parentPlan > 0
                    ? Math.round((parent.ytd_revenue / parentPlan) * 1000) / 10
                    : null;
                const parentProjDelta = parent.year_proj - parent.ly_revenue;
                // A forecast in progress tints the row and shows a progress bar under it.
                const parentRun = forecastRuns[parentId];
                const parentRunning = parentRun?.status === "running";
                const rowBg = parentRunning
                  ? darkMode
                    ? "#1c2a3a"
                    : "#eef5fd"
                  : parentRowBg;

                return (
                  <React.Fragment key={parentId}>
                    {/* ── Parent row ── */}
                    <TableRow
                      sx={{
                        bgcolor: rowBg,
                        cursor: isFlat ? "default" : "pointer",
                        "&:hover": {
                          bgcolor: darkMode ? "#2a2a2a" : "#f0f0f0",
                        },
                      }}
                      onClick={
                        isFlat ? undefined : () => toggleExpand(parentId)
                      }
                    >
                      {/* Category name */}
                      <TableCell sx={{ ...cellSx, bgcolor: rowBg }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {!isFlat && (
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {isExpanded ? (
                                <KeyboardArrowDownIcon
                                  fontSize="small"
                                  sx={{ color: textPrimary }}
                                />
                              ) : (
                                <KeyboardArrowRightIcon
                                  fontSize="small"
                                  sx={{ color: textPrimary }}
                                />
                              )}
                            </IconButton>
                          )}
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: textPrimary,
                            }}
                          >
                            {parent.name}
                          </Typography>
                          {parentRunning && !readOnly && <RunningChip run={parentRun} />}
                          {parent.subcategory_count > 0 && (
                            <Chip
                              label={`${parent.subcategory_count} sub-categories`}
                              size="small"
                              sx={{
                                fontSize: 10,
                                height: 18,
                                bgcolor: darkMode ? "#333" : "#e0e0e0",
                                color: textSecondary,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Parent trend sparkline (actuals + forecast once resolved) */}
                      <TableCell sx={{ ...cellSx, bgcolor: rowBg }}>
                        {renderTrend(parent)}
                      </TableCell>

                      {/* LY */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: textPrimary,
                          }}
                        >
                          {fmtK(parent.ly_revenue)}
                        </Typography>
                      </TableCell>

                      {/* Plan (sum of children or direct) */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: textPrimary,
                          }}
                        >
                          {fmtK(parentPlan)}
                        </Typography>
                      </TableCell>

                      {/* YTD Actual */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: textPrimary,
                          }}
                        >
                          {fmtK(parent.ytd_revenue)}
                        </Typography>
                        {parentYTDPct != null && (
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: parentYTDPct >= 50 ? "#2e7d32" : "#c62828",
                            }}
                          >
                            {fmtPct(parentYTDPct)} vs plan
                          </Typography>
                        )}
                      </TableCell>

                      {/* Year Proj */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: textPrimary,
                          }}
                        >
                          {fmtK(parent.year_proj)}
                        </Typography>
                        {parentProjDelta !== 0 && (
                          <Typography
                            sx={{
                              fontSize: 10,
                              color:
                                parentProjDelta >= 0 ? "#2e7d32" : "#c62828",
                            }}
                          >
                            {fmtDelta(parentProjDelta)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Pace */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.4,
                            px: 1,
                            py: 0.25,
                            borderRadius: 5,
                            bgcolor: paceBg(parent.pace_status),
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: paceColor(parent.pace_status),
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: paceColor(parent.pace_status),
                            }}
                          >
                            {parent.pace_status}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* No status for parent */}
                      {SHOW_STATUS && (
                        <TableCell
                          sx={{
                            ...cellSx,
                            bgcolor: rowBg,
                            textAlign: "center",
                          }}
                        />
                      )}

                      {/* Actions */}
                      <TableCell
                        sx={{
                          ...cellSx,
                          bgcolor: rowBg,
                          textAlign: "center",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActionsMenu
                          run={parentRun}
                          onRun={() => onRunForecast?.(parent)}
                          onView={(run) => onViewForecast?.(run, parent)}
                          onCancel={onCancelForecast}
                          onEmail={onEmailForecast}
                          hideForecast={readOnly}
                          monthlyOpen={isMonthlyOpen(parent)}
                          onToggleMonthly={() => toggleAllMonthly(parent)}
                          textSecondary={textSecondary}
                        />
                      </TableCell>
                    </TableRow>

                    {/* ── Forecast running: a progress bar along the row ── */}
                    {parentRunning && !readOnly && (
                      <TableRow>
                        <TableCell colSpan={spanAll} sx={{ p: 0, borderBottom: "none" }}>
                          <Tooltip
                            title={`Forecasting ${runProgress(parentRun)}`}
                            placement="top"
                          >
                            <LinearProgress
                              variant={parentRun.sku_count ? "determinate" : "indeterminate"}
                              value={
                                parentRun.sku_count
                                  ? Math.min(100, ((parentRun.done_count || 0) / parentRun.sku_count) * 100)
                                  : undefined
                              }
                              sx={{ height: 3 }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* ── Child rows ── */}
                    {isExpanded &&
                      (parent.children || []).map((child) => {
                        const childId = child._id.toString();
                        const isLocked = !!locked[childId];
                        const childPlan = planValues[childId] || 0;
                        const ytdVsPlanPct =
                          childPlan > 0
                            ? Math.round(
                                (child.ytd_revenue / childPlan) * 1000,
                              ) / 10
                            : null;
                        const projDelta = child.year_proj - child.ly_revenue;
                        const projVsLYPct =
                          child.ly_revenue > 0
                            ? Math.round(
                                (projDelta / child.ly_revenue) * 1000,
                              ) / 10
                            : null;

                        return (
                          <React.Fragment key={childId}>
                            <TableRow
                              sx={{
                                bgcolor: childRowBg,
                                "&:hover": {
                                  bgcolor: darkMode ? "#222" : "#fafafa",
                                },
                              }}
                            >
                              {/* Category name (indented) */}
                              <TableCell sx={cellSx}>
                                <Box sx={{ pl: 3.5 }}>
                                  <Typography
                                    sx={{ fontSize: 13, color: textPrimary }}
                                  >
                                    {child.name}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* Trend sparkline */}
                              <TableCell sx={cellSx}>
                                {renderTrend(child)}
                              </TableCell>

                              {/* LY */}
                              <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                                <Typography
                                  sx={{ fontSize: 13, color: textPrimary }}
                                >
                                  {fmtK(child.ly_revenue)}
                                </Typography>
                              </TableCell>

                              {/* Plan input */}
                              <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                                {isLocked ? (
                                  <Typography
                                    sx={{ fontSize: 13, color: textSecondary }}
                                  >
                                    {fmtK(childPlan)}
                                  </Typography>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: 0.25,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: textSecondary,
                                      }}
                                    >
                                      $
                                    </Typography>
                                    <OutlinedInput
                                      size="small"
                                      disabled={readOnly}
                                      value={String(
                                        Math.round(childPlan / 1000),
                                      )}
                                      onChange={(e) =>
                                        handlePlanChange(
                                          childId,
                                          e.target.value,
                                        )
                                      }
                                      endAdornment={
                                        <InputAdornment position="end">
                                          <Typography
                                            sx={{
                                              fontSize: 11,
                                              color: textSecondary,
                                            }}
                                          >
                                            K
                                          </Typography>
                                        </InputAdornment>
                                      }
                                      sx={{
                                        width: 90,
                                        fontSize: 13,
                                        "& .MuiOutlinedInput-input": {
                                          py: 0.5,
                                          px: 0.75,
                                          textAlign: "right",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: darkMode
                                            ? "#444"
                                            : "#ddd",
                                        },
                                      }}
                                    />
                                  </Box>
                                )}
                              </TableCell>

                              {/* YTD Actual */}
                              <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                                <Typography
                                  sx={{ fontSize: 13, color: textPrimary }}
                                >
                                  {fmtK(child.ytd_revenue)}
                                </Typography>
                                {ytdVsPlanPct != null && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color:
                                        ytdVsPlanPct >= 50
                                          ? "#2e7d32"
                                          : "#c62828",
                                    }}
                                  >
                                    {fmtPct(ytdVsPlanPct)} vs plan
                                  </Typography>
                                )}
                              </TableCell>

                              {/* Year Proj */}
                              <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                                <Typography
                                  sx={{ fontSize: 13, color: textPrimary }}
                                >
                                  {fmtK(child.year_proj)}
                                </Typography>
                                {projVsLYPct != null && (
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      color:
                                        projDelta >= 0 ? "#2e7d32" : "#c62828",
                                    }}
                                  >
                                    {fmtDelta(projDelta)} ·{" "}
                                    {fmtPct(projVsLYPct)}
                                  </Typography>
                                )}
                              </TableCell>

                              {/* Pace */}
                              <TableCell
                                sx={{ ...cellSx, textAlign: "center" }}
                              >
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 5,
                                    bgcolor: paceBg(child.pace_status),
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 5,
                                      height: 5,
                                      borderRadius: "50%",
                                      bgcolor: paceColor(child.pace_status),
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: paceColor(child.pace_status),
                                    }}
                                  >
                                    {child.pace_status}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* Status */}
                              {SHOW_STATUS && (
                              <TableCell
                                sx={{ ...cellSx, textAlign: "center" }}
                              >
                                {(() => {
                                  const rawStatus = categoryStatuses?.[childId];
                                  // Map legacy 'active' to 'approved' for backwards compat
                                  const catStatus =
                                    rawStatus === "active"
                                      ? "approved"
                                      : rawStatus === "review"
                                        ? "review"
                                        : rawStatus === "approved"
                                          ? "approved"
                                          : "draft";

                                  const cfg = {
                                    draft: {
                                      label: "Draft",
                                      color: darkMode ? "#94a3b8" : "#475569",
                                      bg: darkMode ? "#2a2a2a" : "#F1F3F5",
                                      dot: darkMode ? "#94a3b8" : "#475569",
                                      border: darkMode ? "#444" : "#CBD5E1",
                                    },
                                    review: {
                                      label: "Review",
                                      color: "#D97706",
                                      bg: darkMode ? "#2a2010" : "#FFFBEB",
                                      dot: "#D97706",
                                      border: "rgba(217,119,6,0.35)",
                                    },
                                    approved: {
                                      label: "Approved",
                                      color: "#16A34A",
                                      bg: darkMode ? "#0d2a18" : "#F0FDF4",
                                      dot: "#16A34A",
                                      border: "rgba(22,163,74,0.3)",
                                    },
                                  };
                                  const c = cfg[catStatus];
                                  return (
                                    <Box
                                      onClick={() =>
                                        planId &&
                                        onCategoryStatusToggle?.(
                                          childId,
                                          catStatus,
                                        )
                                      }
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1.125,
                                        py: 0.375,
                                        borderRadius: 10,
                                        cursor: planId ? "pointer" : "default",
                                        bgcolor: c.bg,
                                        border: `1px solid ${c.border}`,
                                        "&:hover": planId
                                          ? { opacity: 0.8 }
                                          : {},
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 5,
                                          height: 5,
                                          borderRadius: "50%",
                                          bgcolor: c.dot,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 10,
                                          color: c.color,
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.07em",
                                        }}
                                      >
                                        {c.label}
                                      </Typography>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              )}

                              {/* Actions */}
                              <TableCell
                                sx={{ ...cellSx, textAlign: "center" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.25,
                                  }}
                                >
                                  <Tooltip title={isLocked ? "Unlock" : "Lock"}>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleLock(childId)}
                                      disabled={readOnly}
                                      sx={{ color: textSecondary }}
                                    >
                                      {isLocked ? (
                                        <LockIcon fontSize="small" />
                                      ) : (
                                        <LockOpenIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Notes">
                                    <IconButton
                                      size="small"
                                      sx={{ color: textSecondary }}
                                      onClick={() => toggleMonthly(childId)}
                                    >
                                      <ArticleOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      monthlyExpanded[childId]
                                        ? "Hide monthly"
                                        : "Monthly detail"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleMonthly(childId)}
                                      sx={{
                                        color: monthlyExpanded[childId]
                                          ? "#fff"
                                          : textSecondary,
                                        bgcolor: monthlyExpanded[childId]
                                          ? "#1976d2"
                                          : "transparent",
                                        borderRadius: 1,
                                        "&:hover": {
                                          bgcolor: monthlyExpanded[childId]
                                            ? "#1565c0"
                                            : darkMode
                                              ? "#333"
                                              : "#f0f0f0",
                                        },
                                      }}
                                    >
                                      {monthlyExpanded[childId] ? (
                                        <KeyboardArrowUpIcon fontSize="small" />
                                      ) : (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>

                            {/* ── Inline monthly breakdown ── */}
                            {monthlyExpanded[childId] && (
                              <TableRow>
                                <TableCell
                                  colSpan={spanAll}
                                  sx={{
                                    p: 0,
                                    borderBottom: `2px solid #1976d2`,
                                  }}
                                >
                                  <InlineMonthly
                                    category={child}
                                    planValue={childPlan}
                                    currentMonth={currentMonth}
                                    currentYear={currentYearData}
                                    darkMode={darkMode}
                                    planId={planId}
                                    clientId={clientId}
                                    categoryId={childId}
                                    planMonthly={planMonthlyById[childId]}
                                    closedMonth={closedMonth}
                                    avgUnitPrice={savedRowMeta[childId]?.avg_unit_price ?? null}
                                    runForecast={runForecastByLeaf[childId] || null}
                                    onPlanMonthlyChange={onPlanMonthlyChange}
                                    forecastError={
                                      forecastErrors?.[childId] || null
                                    }
                                    forecastPending={forecastPending}
                                    onPlanChange={handlePlanChange}
                                    showActual={showActual}
                                    showForecast={showForecast && !readOnly}
                                    readOnly={readOnly}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    {isExpanded && parent.children?.length > 0 && (
                      <TableRow
                        sx={{ bgcolor: darkMode ? "#232323" : "#f5f5f5" }}
                      >
                        <TableCell
                          sx={{
                            ...cellSx,
                            pl: 3.5,
                            bgcolor: darkMode ? "#232323" : "#f5f5f5",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              color: textSecondary,
                              textTransform: "uppercase",
                            }}
                          >
                            {parent.name} Total
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ bgcolor: darkMode ? "#232323" : "#f5f5f5" }}
                        />
                        <TableCell
                          sx={{
                            ...cellSx,
                            textAlign: "right",
                            bgcolor: darkMode ? "#232323" : "#f5f5f5",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: textPrimary,
                            }}
                          >
                            {fmtK(parent.ly_revenue)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellSx,
                            textAlign: "right",
                            bgcolor: darkMode ? "#232323" : "#f5f5f5",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: textPrimary,
                            }}
                          >
                            {fmtK(parentPlan)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellSx,
                            textAlign: "right",
                            bgcolor: darkMode ? "#232323" : "#f5f5f5",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: textPrimary,
                            }}
                          >
                            {fmtK(parent.ytd_revenue)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellSx,
                            textAlign: "right",
                            bgcolor: darkMode ? "#232323" : "#f5f5f5",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: textPrimary,
                            }}
                          >
                            {fmtK(parent.year_proj)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          colSpan={spanTail}
                          sx={{ bgcolor: darkMode ? "#232323" : "#f5f5f5" }}
                        />
                      </TableRow>
                    )}

                    {/* ── Inline monthly breakdown for a leaf row (no children,
                        e.g. every row in a Product Type plan) ── */}
                    {!parent.children?.length &&
                      monthlyExpanded[parentId] && (
                        <TableRow>
                          <TableCell
                            colSpan={spanAll}
                            sx={{
                              p: 0,
                              borderBottom: `2px solid #1976d2`,
                            }}
                          >
                            <InlineMonthly
                              category={parent}
                              planValue={parentPlan}
                              currentMonth={currentMonth}
                              currentYear={currentYearData}
                              darkMode={darkMode}
                              planId={planId}
                              clientId={clientId}
                              categoryId={parentId}
                              planMonthly={planMonthlyById[parentId]}
                              closedMonth={closedMonth}
                              avgUnitPrice={savedRowMeta[parentId]?.avg_unit_price ?? null}
                              runForecast={runForecastByLeaf[parentId] || null}
                              onPlanMonthlyChange={onPlanMonthlyChange}
                              forecastError={forecastErrors?.[parentId] || null}
                              forecastPending={forecastPending}
                              onPlanChange={handlePlanChange}
                              showActual={showActual}
                              showForecast={showForecast && !readOnly}
                              readOnly={readOnly}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                );
              })}

              {/* ── DropShip Products / SKUs row ── (disabled per request) */}
              {false && activeFilter === "ALL" && (() => {
                const totalDropship = categories.reduce((s, p) => {
                  const parentDs = (p.ytd_dropship_monthly || []).reduce((ps, v) => ps + (v || 0), 0);
                  const childDs = (p.children || []).reduce((cs, c) =>
                    cs + (c.ytd_dropship_monthly || []).reduce((ms, v) => ms + (v || 0), 0), 0,
                  );
                  return s + (p.children?.length ? childDs : parentDs);
                }, 0);
                return (
                  <TableRow sx={{ bgcolor: parentRowBg, opacity: 0.85 }}>
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#7b1fa2" }}>
                        Drop Ship Products / SKUs
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg }} />
                    {/* LY */}
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg, textAlign: "right" }}>
                      <Typography sx={{ fontSize: 13, color: textSecondary }}>—</Typography>
                    </TableCell>
                    {/* Plan */}
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg, textAlign: "right" }}>
                      <Typography sx={{ fontSize: 13, color: textSecondary }}>—</Typography>
                    </TableCell>
                    {/* YTD Actual — accumulated dropship */}
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg, textAlign: "right" }}>
                      {totalDropship > 0 ? (
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#7b1fa2" }}>
                          {fmtK(totalDropship)}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: textSecondary }}>—</Typography>
                      )}
                    </TableCell>
                    {/* Year Proj */}
                    <TableCell sx={{ ...cellSx, bgcolor: parentRowBg, textAlign: "right" }}>
                      <Typography sx={{ fontSize: 13, color: textSecondary }}>—</Typography>
                    </TableCell>
                    {/* Pace, Status, Actions */}
                    <TableCell colSpan={spanTail} sx={{ ...cellSx, bgcolor: parentRowBg }} />
                  </TableRow>
                );
              })()}

              {/* ── Grand total row ── */}
              {filteredCategories.length > 0 && (
                <TableRow sx={{ bgcolor: totalRowBg }}>
                  <TableCell sx={{ ...cellSx, bgcolor: totalRowBg }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: textPrimary,
                        textTransform: "uppercase",
                      }}
                    >
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ bgcolor: totalRowBg }} />
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", bgcolor: totalRowBg }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {fmtK(totalLY)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", bgcolor: totalRowBg }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {fmtK(allocated)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", bgcolor: totalRowBg }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {fmtK(
                        filteredCategories.reduce(
                          (s, p) => s + (p.ytd_revenue || 0),
                          0,
                        ),
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", bgcolor: totalRowBg }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {fmtK(totalYearProj)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={spanTail} sx={{ bgcolor: totalRowBg }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
